import { useState } from 'react';
import { PageHeader } from '@/components/PageHeader';
import { Card, Button, Stepper, Chip } from '@/components/ui';
import { EliteTrainingEngine } from '@/features/training/components/EliteTrainingEngine';
import { Zap, Play } from 'lucide-react';
import { useTrainingStore } from '@/store';

export function StanceMotionPage() {
    const [activeMode, setActiveMode] = useState(false);

    // Local config state (syncs to store on start)
    const [position, setPosition] = useState<'neutral' | 'top' | 'bottom'>('neutral');
    const [workSeconds, setWorkSeconds] = useState(30);
    const [restSeconds, setRestSeconds] = useState(15);
    const [rounds, setRounds] = useState(8);

    const { configure } = useTrainingStore();

    const handleStart = () => {
        // Sync to global store for metrics tracking if needed
        configure({
            workSeconds,
            restSeconds,
            totalRounds: rounds,
            position,
            trainMode: 'stance_motion',
            stanceCommands: getCommandsForPosition(position)
        });
        setActiveMode(true);
    };

    const getCommandsForPosition = (pos: string) => {
        switch (pos) {
            case 'neutral': return ['Stance', 'Motion', 'Circle', 'Level Change', 'Sprawl', 'Shot', 'Downblock'];
            case 'top': return ['Pressure', 'Breakdown', 'Turn', 'Drive', 'Chop', 'Spiral'];
            case 'bottom': return ['Stand Up', 'Sit Out', 'Granby', 'Base Up', 'Hand Control'];
            default: return ['Stance', 'Motion'];
        }
    };

    if (activeMode) {
        return (
            <EliteTrainingEngine
                presetName={`Stance & Motion: ${position.charAt(0).toUpperCase() + position.slice(1)}`}
                workSec={workSeconds}
                restSec={restSeconds}
                rounds={rounds}
                whistleStart={true}
                mode="stance_motion"
                commands={getCommandsForPosition(position)}
            />
        );
    }

    return (
        <div className="space-y-6 pb-24 animate-fade-in">
            <PageHeader title="Stance & Motion" subtitle="Audio-guided movement drills" />

            <div className="px-4 space-y-6">
                {/* Description Card */}
                <div className="p-4 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex gap-4">
                    <div className="shrink-0">
                        <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                            <Zap className="w-5 h-5 text-blue-400" />
                        </div>
                    </div>
                    <div>
                        <h3 className="font-bold text-white text-sm">Interactive Coaching</h3>
                        <p className="text-xs text-blue-200/80 mt-1 leading-relaxed">
                            The app will call out commands based on your position. React instantly to improve reaction time.
                        </p>
                    </div>
                </div>

                {/* Configuration */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between px-1">
                        <span className="text-xs font-bold text-text-muted uppercase tracking-wider">Position</span>
                    </div>
                    <div className="flex gap-2">
                        {(['neutral', 'top', 'bottom'] as const).map(pos => (
                            <Chip
                                key={pos}
                                label={pos.charAt(0).toUpperCase() + pos.slice(1)}
                                active={position === pos}
                                onClick={() => setPosition(pos)}
                            />
                        ))}
                    </div>

                    <Card className="space-y-2 mt-4">
                        <Stepper label="Work" value={workSeconds} onChange={setWorkSeconds} min={10} max={300} step={5} formatValue={v => `${v}s`} />
                        <div className="border-t border-white/[0.05]" />
                        <Stepper label="Rest" value={restSeconds} onChange={setRestSeconds} min={5} max={120} step={5} formatValue={v => `${v}s`} />
                        <div className="border-t border-white/[0.05]" />
                        <Stepper label="Rounds" value={rounds} onChange={setRounds} min={1} max={50} />
                    </Card>
                </div>
            </div>

            {/* Start Button */}
            <div className="fixed bottom-[88px] inset-x-0 px-8 z-40 max-w-md mx-auto">
                <Button onClick={handleStart} size="lg" isHero className="w-full text-lg shadow-button-elite hover:scale-[1.02] active:scale-[0.98]">
                    <Play className="w-5 h-5 fill-current mr-2" /> BEGIN SESSION
                </Button>
            </div>
        </div>
    );
}
